import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { contractsApi, templatesApi, getFullApiUrl } from '../services/api';
import { Contract, Template } from '../types';
import { useAuth } from '../context/AuthContext';
import {
  Plus,
  FileText,
  Clock,
  CheckCircle,
  PenTool,
  AlertCircle,
  Eye,
  Edit,
  Trash2,
  Download,
  Search,
  Calendar,
  AlertTriangle,
  XCircle,
  Archive,
  RotateCcw,
  ArchiveRestore
} from 'lucide-react';

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  DRAFT: { label: 'Entwurf', color: 'bg-gray-100 text-gray-700', icon: Edit },
  SAVED: { label: 'Gespeichert', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  FINALIZED: { label: 'Finalisiert', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  PENDING_REVIEW: { label: 'In Prüfung', color: 'bg-purple-100 text-purple-700', icon: Eye },
  PENDING_SIGNATURE: { label: 'Zur Unterschrift', color: 'bg-orange-100 text-orange-700', icon: PenTool },
  SIGNED: { label: 'Unterschrieben', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  ARCHIVED: { label: 'Archiviert', color: 'bg-gray-100 text-gray-500', icon: FileText }
};

// Funktion zur Berechnung des Befristungs-Status
function getExpirationStatus(expirationDate: string | undefined, isUnlimited: boolean | undefined): {
  label: string;
  color: string;
  urgency: 'none' | 'low' | 'medium' | 'high' | 'critical';
  icon: any;
  daysLeft?: number;
} | null {
  if (isUnlimited) {
    return { label: 'Unbefristet', color: 'bg-gray-100 text-gray-600', urgency: 'none', icon: Calendar };
  }
  
  if (!expirationDate) {
    return null;
  }

  const now = new Date();
  const expDate = new Date(expirationDate);
  const diffTime = expDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Bereits abgelaufen
  if (diffDays < 0) {
    return {
      label: 'Abgelaufen',
      color: 'bg-red-600 text-white animate-pulse',
      urgency: 'critical',
      icon: XCircle,
      daysLeft: diffDays
    };
  }

  // Läuft diesen Monat aus (Alert Rot)
  const thisMonth = now.getMonth();
  const thisYear = now.getFullYear();
  const expMonth = expDate.getMonth();
  const expYear = expDate.getFullYear();
  
  if (thisYear === expYear && thisMonth === expMonth) {
    return {
      label: `Läuft ab (${diffDays} Tage)`,
      color: 'bg-red-500 text-white',
      urgency: 'critical',
      icon: AlertTriangle,
      daysLeft: diffDays
    };
  }

  // Innerhalb 1 Monat (Rot)
  if (diffDays <= 30) {
    return {
      label: `${diffDays} Tage`,
      color: 'bg-red-100 text-red-700 border border-red-300',
      urgency: 'high',
      icon: AlertTriangle,
      daysLeft: diffDays
    };
  }

  // Innerhalb 2 Monate (Gelb)
  if (diffDays <= 60) {
    return {
      label: `${diffDays} Tage`,
      color: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
      urgency: 'medium',
      icon: Clock,
      daysLeft: diffDays
    };
  }

  // Mehr als 2 Monate
  const months = Math.floor(diffDays / 30);
  return {
    label: months > 1 ? `${months} Monate` : `${diffDays} Tage`,
    color: 'bg-green-100 text-green-700',
    urgency: 'low',
    icon: Calendar,
    daysLeft: diffDays
  };
}

export default function ContractsList() {
  const { user } = useAuth();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showArchived, setShowArchived] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const navigate = useNavigate();

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [contractsRes, templatesRes] = await Promise.all([
        contractsApi.getAll({ includeArchived: showArchived }),
        templatesApi.getAll({ active: true })
      ]);
      setContracts(contractsRes.contracts);
      setTemplates(templatesRes.templates);
    } catch (err: any) {
      setError('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Vertrag wirklich löschen?')) return;
    
    try {
      await contractsApi.delete(id);
      setContracts(contracts.filter(c => c.id !== id));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  const handleArchive = async (id: string) => {
    if (!window.confirm('Vertrag archivieren? Er wird aus der Liste ausgeblendet.')) return;
    
    try {
      await contractsApi.archive(id);
      setContracts(contracts.filter(c => c.id !== id));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Archivieren fehlgeschlagen');
    }
  };

  const handleUnarchive = async (id: string) => {
    try {
      await contractsApi.unarchive(id);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Wiederherstellen fehlgeschlagen');
    }
  };

  const handleResetStatus = async (id: string) => {
    if (!window.confirm('Status zurücksetzen? Der Vertrag wird wieder als Entwurf geöffnet.')) return;
    
    try {
      await contractsApi.resetStatus(id);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Zurücksetzen fehlgeschlagen');
    }
  };

  const handlePermanentDelete = async () => {
    if (!showDeleteModal) return;
    
    try {
      await contractsApi.permanentDelete(showDeleteModal, deleteConfirmation, deleteReason || undefined);
      setContracts(contracts.filter(c => c.id !== showDeleteModal));
      setShowDeleteModal(null);
      setDeleteConfirmation('');
      setDeleteReason('');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = contract.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || contract.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Verträge</h1>
          <p className="mt-1 text-sm text-gray-500">
            {contracts.length} Vertrag(e) insgesamt
          </p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Neuer Vertrag
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Vertrag suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            >
              <option value="">Alle Status</option>
              {Object.entries(statusConfig).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <Archive className="h-4 w-4" />
            Archivierte anzeigen
          </label>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {/* Contracts List */}
      {filteredContracts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Verträge gefunden</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || statusFilter
              ? 'Versuchen Sie andere Suchkriterien'
              : 'Erstellen Sie Ihren ersten Vertrag'}
          </p>
          {!searchTerm && !statusFilter && (
            <button
              onClick={() => setShowNewModal(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Vertrag erstellen
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vertrag
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Befristung
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Erstellt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Blöcke
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContracts
                .sort((a, b) => {
                  // Sortiere nach Befristungs-Dringlichkeit
                  const expA = getExpirationStatus(a.expirationDate, a.isUnlimited);
                  const expB = getExpirationStatus(b.expirationDate, b.isUnlimited);
                  
                  const urgencyOrder = { critical: 0, high: 1, medium: 2, low: 3, none: 4 };
                  const urgencyA = expA?.urgency || 'none';
                  const urgencyB = expB?.urgency || 'none';
                  
                  if (urgencyOrder[urgencyA] !== urgencyOrder[urgencyB]) {
                    return urgencyOrder[urgencyA] - urgencyOrder[urgencyB];
                  }
                  
                  // Bei gleicher Dringlichkeit nach Datum sortieren
                  if (expA?.daysLeft !== undefined && expB?.daysLeft !== undefined) {
                    return expA.daysLeft - expB.daysLeft;
                  }
                  
                  return 0;
                })
                .map((contract) => {
                const status = statusConfig[contract.status];
                const StatusIcon = status.icon;
                const expiration = getExpirationStatus(contract.expirationDate, contract.isUnlimited);
                const ExpIcon = expiration?.icon;
                
                return (
                  <tr 
                    key={contract.id} 
                    className={`hover:bg-gray-50 ${
                      expiration?.urgency === 'critical' ? 'bg-red-50' : 
                      expiration?.urgency === 'high' ? 'bg-red-50/50' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <Link
                            to={`/contracts/${contract.id}`}
                            className="text-sm font-medium text-gray-900 hover:text-primary-600"
                          >
                            {contract.title}
                          </Link>
                          {contract.template && (
                            <p className="text-xs text-gray-500">
                              Vorlage: {contract.template.name}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {expiration ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${expiration.color}`}>
                          {ExpIcon && <ExpIcon className="h-3 w-3 mr-1" />}
                          {expiration.label}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                      {contract.expirationDate && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {new Date(contract.expirationDate).toLocaleDateString('de-AT')}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(contract.createdAt).toLocaleDateString('de-AT')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {contract._count?.blocks || 0}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/contracts/${contract.id}`}
                          className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                          title="Anzeigen"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        {/* Bearbeiten - nur für nicht-unterschriebene */}
                        {['DRAFT', 'SAVED', 'FINALIZED', 'PENDING_REVIEW', 'PENDING_SIGNATURE'].includes(contract.status) && !contract.isArchived && (
                          <Link
                            to={`/contracts/${contract.id}/edit`}
                            className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                            title="Bearbeiten"
                          >
                            <Edit className="h-4 w-4" />
                          </Link>
                        )}
                        
                        {/* Status zurücksetzen - nur für nicht-unterschriebene */}
                        {['FINALIZED', 'PENDING_REVIEW', 'PENDING_SIGNATURE'].includes(contract.status) && !contract.isArchived && (
                          <button
                            onClick={() => handleResetStatus(contract.id)}
                            className="p-1.5 text-gray-400 hover:text-orange-600 rounded"
                            title="Status zurücksetzen (zu Entwurf)"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* PDF Download */}
                        <a
                          href={getFullApiUrl(`/pdf/${contract.id}?download=true`, true)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                          title="PDF herunterladen"
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        
                        {/* Archivieren - für unterschriebene Verträge */}
                        {contract.status === 'SIGNED' && !contract.isArchived && (
                          <button
                            onClick={() => handleArchive(contract.id)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 rounded"
                            title="Archivieren"
                          >
                            <Archive className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* Aus Archiv wiederherstellen - nur für Admins */}
                        {contract.isArchived && isAdmin && (
                          <button
                            onClick={() => handleUnarchive(contract.id)}
                            className="p-1.5 text-gray-400 hover:text-green-600 rounded"
                            title="Aus Archiv wiederherstellen"
                          >
                            <ArchiveRestore className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* Normales Löschen - nur für Entwürfe */}
                        {['DRAFT', 'SAVED'].includes(contract.status) && !contract.isArchived && (
                          <button
                            onClick={() => handleDelete(contract.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                            title="Löschen"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                        
                        {/* Permanentes Löschen - nur für Admins */}
                        {isAdmin && (
                          <button
                            onClick={() => setShowDeleteModal(contract.id)}
                            className="p-1.5 text-gray-400 hover:text-red-700 rounded"
                            title="Permanent löschen (Admin)"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* New Contract Modal */}
      {showNewModal && (
        <NewContractModal
          templates={templates}
          onClose={() => setShowNewModal(false)}
          onCreate={(contract) => {
            navigate(`/contracts/${contract.id}/edit`);
          }}
        />
      )}

      {/* Permanent Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Vertrag permanent löschen
                </h2>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Diese Aktion kann <strong>nicht rückgängig</strong> gemacht werden. 
                Der Vertrag und alle zugehörigen Daten werden unwiderruflich gelöscht.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zur Bestätigung "LÖSCHEN" eingeben
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder="LÖSCHEN"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Löschgrund (optional)
                  </label>
                  <textarea
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Warum wird dieser Vertrag gelöscht?"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(null);
                    setDeleteConfirmation('');
                    setDeleteReason('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handlePermanentDelete}
                  disabled={deleteConfirmation !== 'LÖSCHEN'}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Permanent löschen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// New Contract Modal Component
function NewContractModal({
  templates,
  onClose,
  onCreate
}: {
  templates: Template[];
  onClose: () => void;
  onCreate: (contract: Contract) => void;
}) {
  const [title, setTitle] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [expirationDate, setExpirationDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Titel erforderlich');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const { contract } = await contractsApi.create({
        title: title.trim(),
        templateId: templateId || undefined,
        isUnlimited,
        expirationDate: !isUnlimited && expirationDate ? expirationDate : undefined
      });
      onCreate(contract);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erstellen fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Neuen Vertrag erstellen
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vertragstitel
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="z.B. Mietvertrag Mustermann"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vorlage (optional)
              </label>
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Keine Vorlage / Leer beginnen</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              {templateId && (
                <p className="mt-1 text-xs text-gray-500">
                  Die Bauteile der Vorlage werden in den neuen Vertrag übernommen.
                </p>
              )}
            </div>

            {/* Befristung */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Befristung
              </label>
              
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  id="isUnlimited"
                  checked={isUnlimited}
                  onChange={(e) => setIsUnlimited(e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="isUnlimited" className="ml-2 text-sm text-gray-700">
                  Unbefristeter Vertrag
                </label>
              </div>

              {!isUnlimited && (
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Ablaufdatum
                  </label>
                  <input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Sie werden vor Ablauf des Vertrags benachrichtigt
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isLoading ? 'Erstelle...' : 'Vertrag erstellen'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
